import { cfgLiveChatLicenseId } from '@infinitro/config';
import { get } from 'svelte/store';
import { stateUsername } from '../auth';
const LIVECHAT_LICENSE = cfgLiveChatLicenseId() ?? '';
let currentUsername = '';
let pendingOpen = false;
const hasLiveChatScriptTag = () => typeof document !== 'undefined' &&
    !!document.querySelector('script[src*="cdn.livechatinc.com/tracking.js"]');
const installLiveChatStub = () => {
    if (!LIVECHAT_LICENSE || typeof window === 'undefined' || typeof document === 'undefined')
        return;
    if (window.LiveChatWidget)
        return;
    window.__lc = window.__lc || {};
    window.__lc.license = LIVECHAT_LICENSE;
    (function (n, t, c) {
        function i(item) {
            return e._h ? e._h.apply(null, item) : e._q.push(item);
        }
        const e = {
            _q: [],
            _h: null,
            _v: '2.0',
            on: function () {
                i(['on', c.call(arguments)]);
            },
            once: function () {
                i(['once', c.call(arguments)]);
            },
            off: function () {
                i(['off', c.call(arguments)]);
            },
            get: function () {
                if (!e._h)
                    throw new Error("[LiveChatWidget] You can't use getters before load.");
                return i(['get', c.call(arguments)]);
            },
            call: function () {
                i(['call', c.call(arguments)]);
            },
            init: function () {
                if (hasLiveChatScriptTag())
                    return;
                const script = t.createElement('script');
                script.async = true;
                script.type = 'text/javascript';
                script.src = 'https://cdn.livechatinc.com/tracking.js';
                script.onerror = () => {
                    console.log('Failed to load LiveChat script.');
                };
                t.head.appendChild(script);
            }
        };
        n.LiveChatWidget = n.LiveChatWidget || e;
    })(window, document, Array.prototype.slice);
};
const ensureLiveChatScriptLoaded = () => {
    installLiveChatStub();
    if (!window.LiveChatWidget || window.__lc?.asyncInit)
        return;
    if (!hasLiveChatScriptTag()) {
        window.LiveChatWidget.init();
    }
};
const setCustomerName = (username) => {
    if (typeof window.LiveChatWidget?.call !== 'function')
        return;
    try {
        window.LiveChatWidget.call('set_customer_name', username ?? '');
    }
    catch (error) {
        console.log('Failed to set LiveChat customer name:', error);
    }
};
const setCustomerData = (username) => {
    if (!username)
        return;
    setCustomerName(username);
    if (typeof window.LiveChatWidget?.call !== 'function')
        return;
    try {
        window.LiveChatWidget.call('set_session_variables', { username });
    }
    catch (error) {
        console.log('Failed to set LiveChat session variables:', error);
    }
};
const clearCustomerData = () => {
    setCustomerName('');
    if (typeof window.LiveChatWidget?.call !== 'function')
        return;
    try {
        window.LiveChatWidget.call('set_session_variables', { username: '' });
    }
    catch (error) {
        console.log('Failed to clear LiveChat session variables:', error);
    }
};
const getLiveChatVisibility = () => {
    if (!window.LiveChatReady || typeof window.LiveChatWidget?.get !== 'function') {
        return undefined;
    }
    try {
        const state = window.LiveChatWidget.get('state');
        return state?.visibility;
    }
    catch {
        return window.LiveChatVisible ? 'maximized' : 'hidden';
    }
};
const isLiveChatMaximized = () => {
    if (window.LiveChatVisible === true)
        return true;
    return getLiveChatVisibility() === 'maximized';
};
const openLiveChat = () => {
    const username = get(stateUsername);
    if (username) {
        setCustomerData(username);
    }
    const maximize = () => {
        window.LiveChatWidget?.call('maximize');
    };
    // Defer so a concurrent hide() on ready does not cancel maximize.
    if (typeof requestAnimationFrame === 'function') {
        requestAnimationFrame(maximize);
    }
    else {
        setTimeout(maximize, 0);
    }
};
const hideLiveChatLauncher = () => {
    try {
        window.LiveChatWidget?.call('hide');
    }
    catch (error) {
        console.log('Failed to hide LiveChat launcher:', error);
    }
};
const onLiveChatReady = () => {
    window.LiveChatReady = true;
    const username = get(stateUsername);
    if (username) {
        setCustomerData(username);
    }
    if (pendingOpen) {
        pendingOpen = false;
        openLiveChat();
        return;
    }
    hideLiveChatLauncher();
};
const onLiveChatVisibilityChanged = (data) => {
    window.LiveChatVisible = data.visibility === 'maximized';
};
export const handleLiveChat = () => {
    if (!LIVECHAT_LICENSE) {
        return;
    }
    ensureLiveChatScriptLoaded();
    if (typeof window.LiveChatWidget?.call !== 'function') {
        console.warn('LiveChat is not available.');
        return;
    }
    if (!window.LiveChatReady) {
        pendingOpen = true;
        return;
    }
    try {
        if (isLiveChatMaximized()) {
            window.LiveChatWidget.call('hide');
            return;
        }
        openLiveChat();
    }
    catch (error) {
        console.log('Failed to handle LiveChat:', error);
    }
};
export const shutdownLiveChat = () => {
    if (typeof window.LiveChatWidget?.call !== 'function')
        return;
    try {
        window.LiveChatWidget.call('hide');
        clearCustomerData();
        pendingOpen = false;
        window.LiveChatVisible = false;
    }
    catch (error) {
        console.log('Failed to shutdown LiveChat:', error);
    }
};
if (typeof window !== 'undefined' && LIVECHAT_LICENSE) {
    ensureLiveChatScriptLoaded();
    window.LiveChatWidget?.on('ready', onLiveChatReady);
    window.LiveChatWidget?.on('visibility_changed', onLiveChatVisibilityChanged);
    stateUsername.subscribe((username) => {
        if (username !== currentUsername) {
            currentUsername = username;
            if (!username) {
                shutdownLiveChat();
            }
            else if (window.LiveChatReady) {
                setCustomerData(username);
            }
        }
    });
}
